import {AgentNs, summaryText} from '@shared/types';

// ─── Small utils ──────────────────────────────────────────────

function flattenPages(s: AgentNs.Structure | null): AgentNs.Page[] {
  return s?.pages || [];
}

function flattenModules(s: AgentNs.Structure | null): AgentNs.Module[] {
  if (!s) return [];
  return [
    ...(s.sections?.flatMap(sec => sec.modules || []) || []),
    ...(s.modules || []),
  ];
}

function summaryEqual(a?: string, b?: string): boolean {
  return (a ?? '').trim() === (b ?? '').trim();
}

function openPointsEqual(a?: string[], b?: string[]): boolean {
  const sa = JSON.stringify(a || []);
  const sb = JSON.stringify(b || []);
  return sa === sb;
}

/**
 * Append the backend's single new summary entry to a node's history, mirroring
 * `BuilderStateProjector.appendOneSummaryEntry`. `summary[]` is append-only: replacing it here
 * would drop the history the card renders as "previous summary", and taking an empty array would
 * blank the description the user already had (F5-T5.2 review finding 1).
 */
function appendSummary(
  existing: AgentNs.SummaryEntry[] | undefined,
  incoming: AgentNs.SummaryEntry[] | undefined,
): AgentNs.SummaryEntry[] {
  const history = [...(existing || [])];
  const next = incoming?.[incoming.length - 1];
  if (!next?.text) return history;
  if (history[history.length - 1]?.text === next.text) return history;
  history.push(next);
  return history;
}

/** A feature delta with no delta slots is a creation carrying the full shape. */
function isNewFeatureDelta(delta: AgentNs.FeatureDelta): delta is AgentNs.NewFeatureDelta {
  const update = delta as AgentNs.UpdatedFeatureDelta;
  return update.added_open_points === undefined && update.resolved_open_points === undefined;
}

// ─── Public: compute diff ─────────────────────────────────────

/** Compare `previous` and `proposed` structures and return diff buckets. */
export function computeDiff(
  previous: AgentNs.Structure | null,
  proposed: AgentNs.Structure,
): AgentNs.StructureDiff {
  const prevById = new Map<string, AgentNs.Module | AgentNs.Page>();
  for (const p of (previous?.pages || [])) { if (p.id) prevById.set(p.id, p); }
  for (const m of flattenModules(previous)) { if (m.id) prevById.set(m.id, m); }

  const proposedPages = flattenPages(proposed);
  const proposedMods = flattenModules(proposed);

  const newList: AgentNs.DiffEntry[] = [];
  const updated: AgentNs.DiffEntry[] = [];
  const unchanged: AgentNs.DiffEntry[] = [];

  const classify = (item: AgentNs.Module | AgentNs.Page, type: 'page' | 'module'): void => {
    if (!item?.id) return;
    const prev = prevById.get(item.id);
    const entry: AgentNs.DiffEntry = {id: item.id, name: item.name, type};

    if (!prev) {
      newList.push(entry);
      return;
    }

    const nameChanged = (prev.name || '').toLowerCase() !== (item.name || '').toLowerCase();
    const summaryChanged = !summaryEqual(summaryText(prev), summaryText(item));
    const openPointsChanged = !openPointsEqual(prev.open_points, item.open_points);
    // Feature-level compare — only relevant for modules.
    let featuresChanged = false;
    if (type === 'module') {
      const prevFeatures = (prev as AgentNs.Module).features || [];
      const nextFeatures = (item as AgentNs.Module).features || [];
      featuresChanged = JSON.stringify(prevFeatures) !== JSON.stringify(nextFeatures);
    }

    if (nameChanged || summaryChanged || openPointsChanged || featuresChanged) {
      updated.push(entry);
    } else {
      unchanged.push(entry);
    }
  };

  proposedPages.forEach(p => classify(p, 'page'));
  proposedMods.forEach(m => classify(m, 'module'));

  return {new: newList, updated, unchanged};
}

// ─── Public: annotate modules ─────────────────────────────────

/** Walk the proposed structure and tag every module / page / feature with… */
export function annotateModulesWithDiff(
  proposed: AgentNs.Structure,
  previous: AgentNs.Structure | null,
  diff: AgentNs.StructureDiff,
): void {
  const newIds = new Set((diff.new || []).map(e => e.id));
  const updatedIds = new Set((diff.updated || []).map(e => e.id));

  const prevById = new Map<string, AgentNs.Module | AgentNs.Page>();
  for (const p of (previous?.pages || [])) { if (p.id) prevById.set(p.id, p); }
  for (const sec of (previous?.sections || [])) {
    for (const m of (sec.modules || [])) { if (m.id) prevById.set(m.id, m); }
  }
  for (const m of (previous?.modules || [])) { if (m.id) prevById.set(m.id, m); }

  const annotate = (item: AgentNs.Module | AgentNs.Page): void => {
    if (!item?.id) return;
    if (newIds.has(item.id)) {
      item._diffStatus = 'new';
      item._changes = 'New';
    } else if (updatedIds.has(item.id)) {
      item._diffStatus = 'updated';
      const prev = prevById.get(item.id);
      item._prevSummary = summaryText(prev);
      item._changes = describeChange(prev, item);
    } else {
      item._diffStatus = null;
    }
  };

  (proposed.pages || []).forEach(annotate);
  (proposed.sections || []).forEach(sec => (sec.modules || []).forEach(annotate));
  (proposed.modules || []).forEach(annotate);

  const annotateFeatures = (proposedMod: AgentNs.Module): void => {
    if (proposedMod._diffStatus !== 'updated') return;
    const prev = prevById.get(proposedMod.id) as AgentNs.Module | undefined;
    if (!prev) return;
    const oldById = new Map((prev.features || []).map(f => [f.id, f]));
    (proposedMod.features || []).forEach(f => {
      const old = oldById.get(f.id);
      if (!old) {
        f._diffStatus = 'new';
        f._changes = 'New';
      } else if (old.name !== f.name) {
        f._diffStatus = 'updated';
        f._changes = `Renamed from ${old.name}`;
        f._prevSummary = summaryText(old);
      } else if (!summaryEqual(summaryText(old), summaryText(f))) {
        f._diffStatus = 'updated';
        f._changes = 'Summary refined';
        f._prevSummary = summaryText(old);
      } else if (!openPointsEqual(old.open_points, f.open_points)) {
        f._diffStatus = 'updated';
        f._changes = 'Open points updated';
        f._prevSummary = summaryText(old);
      } else {
        f._diffStatus = null;
      }
    });
  };

  (proposed.sections || []).forEach(sec => (sec.modules || []).forEach(annotateFeatures));
  (proposed.modules || []).forEach(annotateFeatures);
}

function describeChange(
  prev: AgentNs.Module | AgentNs.Page | undefined,
  next: AgentNs.Module | AgentNs.Page,
): string {
  if (!prev) return 'New';
  const prevName = (prev.name || '').toLowerCase();
  const nextName = (next.name || '').toLowerCase();
  if (prevName && nextName && prevName !== nextName) {
    return `Renamed from ${prev.name}`;
  }
  if (!summaryEqual(summaryText(prev), summaryText(next))) return 'Summary refined';
  if (!openPointsEqual(prev.open_points, next.open_points)) return 'Open points updated';
  // Feature-level change on a module
  if ((prev as AgentNs.Module).features || (next as AgentNs.Module).features) {
    return 'Features updated';
  }
  return 'Updated';
}

// ─── Public: resolve items by bucket for rendering ────────────

/** Return Module/Page objects referenced by a DiffEntry list, in original order. */
export function resolveDiffItems(
  structure: AgentNs.Structure,
  entries: AgentNs.DiffEntry[],
): { pages: AgentNs.Page[]; modules: AgentNs.Module[] } {
  const pages: AgentNs.Page[] = [];
  const modules: AgentNs.Module[] = [];
  const allPages = structure.pages || [];
  const allMods = [
    ...(structure.sections?.flatMap(s => s.modules || []) || []),
    ...(structure.modules || []),
  ];

  entries.forEach(e => {
    if (!e?.id) return;
    const isPage = e.type === 'page' || allPages.some(p => p.id === e.id);
    if (isPage) {
      const found = allPages.find(p => p.id === e.id);
      if (found) pages.push(found);
    } else {
      const found = allMods.find(m => m.id === e.id);
      if (found) modules.push(found);
    }
  });

  return {pages, modules};
}

/** Merge open-point delta against an existing list. Module-scoped. */
export function mergeOpenPoints(
  existing: string[] | undefined,
  delta: { added_open_points?: string[]; resolved_open_points?: string[] },
): string[] {
  const added = delta.added_open_points || [];
  const resolved = delta.resolved_open_points || [];
  const kept = (existing || []).filter(a => !resolved.includes(a));
  return [...kept, ...added];
}

/** Apply a BUILDER_MODE_DIFF payload to an existing structure. */
export function applyDiffPayload(
  existing: AgentNs.Structure,
  payload: AgentNs.DiffPayload,
): AgentNs.Structure {
  const merged: AgentNs.Structure = JSON.parse(JSON.stringify(existing));

  const newEntries: AgentNs.DiffEntry[] = [];
  const updatedEntries: AgentNs.DiffEntry[] = [];
  const touchedIds = new Set<string>();

  // 1. Apply updates to existing modules
  (payload.updated_modules || []).forEach(update => {
    const mod = findModuleById(merged, update.id);
    if (!mod) return;

    if (update.name) mod.name = update.name;
    mod.summary = appendSummary(mod.summary, update.summary);

    // Open-point delta — verbatim text match, module-scoped
    mod.open_points = mergeOpenPoints(mod.open_points, update);

    // Feature delta
    if (update.features && update.features.length > 0) {
      mod.features = applyFeaturesDelta(mod.features || [], update.features);
    }

    mod._diffStatus = 'updated';
    mod._diffDelta = {
      added_count: (update.added_open_points || []).length,
      resolved_count: (update.resolved_open_points || []).length,
      resolved_texts: update.resolved_open_points || [],
    };
    updatedEntries.push({id: mod.id, name: mod.name, type: 'module'});
    touchedIds.add(mod.id);
  });

  // 2. Append new modules to their target sections
  (payload.new_modules || []).forEach(nm => {
    const newMod: AgentNs.Module = {
      id: nm.id,
      name: nm.name,
      summary: nm.summary || [],
      color: '',
      open_points: nm.open_points || [],
      features: (nm.features || []).map(f => ({
        id: f.id,
        name: f.name,
        summary: f.summary || [],
        open_points: f.open_points || [],
        _diffStatus: 'new',
      })),
      _diffStatus: 'new',
      _diffDelta: {
        added_count: (nm.open_points || []).length,
        resolved_count: 0,
      },
    };

    const section = (merged.sections || []).find(s => s.id === nm.section_id);
    if (section) {
      section.modules.push(newMod);
    } else {
      merged.modules = [...(merged.modules || []), newMod];
    }

    newEntries.push({id: newMod.id, name: newMod.name, type: 'module'});
    touchedIds.add(newMod.id);
  });

  // 3. Build unchanged bucket — every module in merged not in touchedIds
  const unchangedEntries: AgentNs.DiffEntry[] = [];
  const allModules = [
    ...(merged.sections?.flatMap(s => s.modules || []) || []),
    ...(merged.modules || []),
  ];
  allModules.forEach(m => {
    if (!touchedIds.has(m.id)) {
      unchangedEntries.push({id: m.id, name: m.name, type: 'module'});
    }
  });

  merged.diff = {
    new: newEntries,
    updated: updatedEntries,
    unchanged: unchangedEntries,
  };

  return merged;
}

function findModuleById(structure: AgentNs.Structure, id: string): AgentNs.Module | undefined {
  for (const sec of structure.sections || []) {
    const found = (sec.modules || []).find(m => m.id === id);
    if (found) return found;
  }
  return (structure.modules || []).find(m => m.id === id);
}

function applyFeaturesDelta(
  existing: AgentNs.Feature[],
  updates: AgentNs.FeatureDelta[],
): AgentNs.Feature[] {
  const merged = [...existing];

  updates.forEach(entry => {
    // A creation carries the full shape — its own summary and its complete open_points. Deriving
    // those from `added_open_points` lost both, and the lossy result was then committed.
    if (isNewFeatureDelta(entry)) {
      merged.push({
        id: entry.id,
        name: entry.name,
        summary: entry.summary || [],
        open_points: entry.open_points || [],
        _diffStatus: 'new',
        _diffDelta: {
          added_count: (entry.open_points || []).length,
          resolved_count: 0,
        },
      });
      return;
    }

    const uf = entry;
    const idx = merged.findIndex(f => f.id === uf.id);

    // An update whose id is not in the local copy: the backend already proved the id exists, so
    // render what the delta carries rather than dropping it.
    if (idx < 0) {
      merged.push({
        id: uf.id,
        name: uf.name || '',
        summary: appendSummary([], uf.summary),
        open_points: uf.added_open_points || [],
        _diffStatus: 'new',
        _diffDelta: {
          added_count: (uf.added_open_points || []).length,
          resolved_count: 0,
        },
      });
      return;
    }

    const feat = merged[idx];
    if (!feat) return;
    if (uf.name) feat.name = uf.name;
    feat.summary = appendSummary(feat.summary, uf.summary);
    feat.open_points = mergeOpenPoints(feat.open_points, uf);
    feat._diffStatus = 'updated';
    feat._diffDelta = {
      added_count: (uf.added_open_points || []).length,
      resolved_count: (uf.resolved_open_points || []).length,
      resolved_texts: uf.resolved_open_points || [],
    };
  });

  return merged;
}

// ─── Feature diff summary ─────────────────────────────────────

/** For an UPDATED module. */
export function splitFeaturesByDiff(module: AgentNs.Module): {
  newFeatures: AgentNs.Feature[];
  updatedFeatures: AgentNs.Feature[];
  unchangedCount: number;
} {
  const features = module.features || [];
  const newFeatures: AgentNs.Feature[] = [];
  const updatedFeatures: AgentNs.Feature[] = [];
  let unchangedCount = 0;

  features.forEach(f => {
    if (f._diffStatus === 'new') newFeatures.push(f);
    else if (f._diffStatus === 'updated') updatedFeatures.push(f);
    else unchangedCount++;
  });

  return {newFeatures, updatedFeatures, unchangedCount};
}
